import React from 'react';
import styled from 'styled-components';
import { Border, Breakpoints, Color, Grid, Shadow, Transition, Type } from '../../../app/StyleGuide';
import { SecondaryBadge } from '../../../components/Badge';
import { SmallRadioRoundedIconButton } from '../../../components/Buttons';
import { ChurchImage } from '../../../components/ChurchImage';
import { RaisedContainer } from '../../../components/Layout';

type ChurchItemProps = {
  name: string;
  meta: React.ReactNode;
  logo?: string;
  disabled?: boolean;
  onSelect?: () => void;
  children?: React.ReactNode;
};

type ChurchSelectableItemProps = {
  name: string;
  meta: React.ReactNode;
  logo?: string;
  isSelected?: boolean;
  onSelect: () => void;
};

type ChurchConnectedItemProps = {
  name: string;
  meta: React.ReactNode;
  logo?: string;
  connectedLabel?: string;
};

const ChurchItemContainer = styled(RaisedContainer)<{ $isClickable?: boolean; $hasTrailingContent?: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: ${props => (props.$hasTrailingContent ? 'auto 1fr auto' : 'auto 1fr')};
  gap: ${Grid._3};
  align-items: center;
  text-align: left;
  padding: ${Grid._4};
  width: 100%;
  border: 1px solid ${Color.Gray._20};
  border-radius: ${Border.radius};
  box-shadow: ${Shadow.small};
  transition: ${Transition.fast};

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    gap: ${Grid._4};
  }

  &[aria-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.5;
  }

  ${props =>
    props.$isClickable &&
    `
    &:not([aria-disabled='true']):hover {
      border-color: ${Color.Gray._80};
      box-shadow: none;
      cursor: pointer;
    }
  `}
`;

const ChurchDetails = styled.div`
  display: grid;
  gap: ${Grid._1};
`;

const ChurchName = styled.h5`
  margin: 0;
  font-size: ${Type.Scale._2};
  font-weight: ${Type.Weight.semibold};
  color: ${Type.Color.dark};

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    font-size: ${Type.Scale._3};
  }
`;

const ChurchMeta = styled.span`
  font-size: ${Type.Scale._1};
  color: ${Type.Color.medium};

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    font-size: ${Type.Scale._1};
  }
`;

const ConnectedIndicator = styled(SecondaryBadge)`
  position: absolute;
  top: 0;
  right: 0;

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    top: ${Grid._3};
    right: ${Grid._3};
  }
`;

export const ChurchItem = ({ name, meta, logo, disabled, onSelect, children }: ChurchItemProps) => {
  const isClickable = Boolean(onSelect) && !disabled;

  const handleSelect = () => {
    if (disabled) return;
    onSelect?.();
  };

  return (
    <ChurchItemContainer
      $isClickable={isClickable}
      $hasTrailingContent={Boolean(children)}
      aria-disabled={Boolean(disabled)}
      onClick={handleSelect}
    >
      <ChurchImage name={name} logo={logo} />
      <ChurchDetails>
        <ChurchName>{name}</ChurchName>
        <ChurchMeta>{meta}</ChurchMeta>
      </ChurchDetails>

      {children}
    </ChurchItemContainer>
  );
};

export const ChurchSelectableItem = ({ name, meta, logo, isSelected = false, onSelect }: ChurchSelectableItemProps) => (
  <ChurchItem name={name} meta={meta} logo={logo} onSelect={onSelect}>
    <SmallRadioRoundedIconButton
      aria-label={`Select ${name}`}
      onClick={event => {
        event.stopPropagation();
        onSelect();
      }}
    >
      {isSelected ? <i className="fas fa-circle color-complete" /> : <i />}
    </SmallRadioRoundedIconButton>
  </ChurchItem>
);

export const ChurchConnectedItem = ({
  name,
  meta,
  logo,
  connectedLabel = 'Already Connected',
}: ChurchConnectedItemProps) => (
  <ChurchItem name={name} meta={meta} logo={logo} disabled>
    <ConnectedIndicator>{connectedLabel}</ConnectedIndicator>
  </ChurchItem>
);
